/**
 * Hook to load missing documents from Medplum Task resources plus inferred rows from
 * fulfilled Appointments without an appointment-tagged QuestionnaireResponse, in live mode,
 * or from local mock data in mock mode.
 *
 * FHIR model:
 *   Task.code.coding[0]        → category (missing-notes | treatment-plans | forms)
 *   Task.code.coding[1]        → form subtype (roi | custody-agreement | intake | discharge-summary)
 *   Task.owner                 → clinician responsible
 *   Task.for / Task.subject    → patient
 *   Task.restriction.period.end → due date
 *   Task.description           → display text
 *   Task.status                → requested=overdue/due-soon, completed, cancelled
 *   Task.authoredOn            → session date / creation date
 *   Task.category              → supervision task marker
 */

import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { MedplumClient } from '@medplum/core';
import type { Task } from '@medplum/fhirtypes';
import { MISSING_NOTE_TASK_CODE, TASK_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import type {
  MissingDocumentCategory,
  MissingDocumentFormType,
  MissingDocumentItem,
  MissingDocumentStatus,
  Supervisee,
} from '../types';
import {
  SUPERVISION_TASK_CATEGORY_CODE,
  SUPERVISION_TASK_CATEGORY_SYSTEM,
  SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM,
  SUPERVISION_TASK_SYSTEM,
} from '../supervisionFhirConstants';
import {
  fetchMissingProgressNotesFromAppointments,
  filterAppointmentItemsDedupedAgainstTasks,
} from '../utils/missingDocumentsFromAppointments';
import { useSupervisionScope } from './useSupervisionScope';
import { loadPractitionerStaffLabelMap } from '@/lib/loadPractitionerPreferredLabels';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

function extractRefId(ref: string | undefined): string | undefined {
  if (!ref) return undefined;
  const match = ref.match(/\w+\/([^/]+)$/);
  return match?.[1];
}

function taskToMissingDocumentItem(task: Task): MissingDocumentItem | null {
  const categoryCoding = task.code?.coding?.find((c) => c.system === SUPERVISION_TASK_SYSTEM);
  const isMissingNoteBotTask = task.code?.coding?.some(
    (c) => c.system === TASK_TYPE_SYSTEM && c.code === MISSING_NOTE_TASK_CODE,
  );
  if (!categoryCoding?.code && !isMissingNoteBotTask) return null;

  const category = (categoryCoding?.code ?? 'missing-notes') as MissingDocumentCategory;
  const formSubtypeCoding = task.code?.coding?.find((c) => c.system === SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM);
  const formType = formSubtypeCoding?.code as MissingDocumentFormType | undefined;

  const dueDate = task.restriction?.period?.end;
  const now = new Date();
  let status: MissingDocumentStatus = 'due-soon';
  if (dueDate && new Date(dueDate) < now) {
    status = 'overdue';
  }

  const focusRef = task.focus?.reference;
  const appointmentId =
    focusRef?.startsWith('Appointment/') ? extractRefId(focusRef) : undefined;
  const documentId =
    focusRef && !focusRef.startsWith('Appointment/') ? extractRefId(focusRef) : undefined;

  return {
    id: task.id!,
    category,
    formType: category === 'forms' ? formType : undefined,
    clinicianId: extractRefId(task.owner?.reference) ?? '',
    clinicianName: task.owner?.display ?? '—',
    clientId: extractRefId(task.for?.reference),
    clientName: task.for?.display ?? '—',
    appointmentId,
    sessionDate: task.authoredOn?.slice(0, 10),
    dueDate: dueDate?.slice(0, 10),
    status,
    description: task.description,
    documentId,
  };
}

function deriveSuperviseesFromItems(items: MissingDocumentItem[]): Supervisee[] {
  const byId = new Map<string, { name: string; count: number; overdueCount: number }>();
  for (const item of items) {
    const existing = byId.get(item.clinicianId);
    if (existing) {
      existing.count += 1;
      if (item.status === 'overdue') existing.overdueCount += 1;
    } else {
      byId.set(item.clinicianId, {
        name: item.clinicianName,
        count: 1,
        overdueCount: item.status === 'overdue' ? 1 : 0,
      });
    }
  }
  return [...byId.entries()].map(([id, data]) => ({
    id,
    name: data.name,
    role: 'Clinician',
    unsignedCount: data.count,
    overdueCount: data.overdueCount,
  }));
}

async function searchTasksOrEmpty(
  medplum: MedplumClient,
  query: Record<string, string>,
): Promise<Task[]> {
  try {
    return await medplum.searchResources('Task', query);
  } catch {
    return [];
  }
}

async function fetchMissingDocumentTasks(
  medplum: MedplumClient,
  scopedClinicianIds: string[],
): Promise<Task[]> {
  const [tagged, ...owned] = await Promise.all([
    searchTasksOrEmpty(medplum, {
      _tag: `${SUPERVISION_TASK_CATEGORY_SYSTEM}|${SUPERVISION_TASK_CATEGORY_CODE}`,
      status: 'requested,in-progress',
      _sort: 'period',
      _count: '200',
    }),
    ...scopedClinicianIds.map((id) =>
      searchTasksOrEmpty(medplum, {
        owner: `Practitioner/${id}`,
        status: 'requested,in-progress',
        _count: '100',
      }),
    ),
  ]);
  const byId = new Map<string, Task>();
  for (const task of [...tagged, ...owned.flat()]) {
    if (task.id) byId.set(task.id, task);
  }
  return [...byId.values()];
}

export interface UseMissingDocumentsResult {
  items: MissingDocumentItem[];
  supervisees: Supervisee[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useMissingDocumentsFromMedplum(): UseMissingDocumentsResult {
  const medplum = useMedplum();
  const { scopeReady, scopedClinicianIds, viewerPractitionerId, superviseeOptions } = useSupervisionScope();
  const [items, setItems] = useState<MissingDocumentItem[]>([]);
  const [supervisees, setSupervisees] = useState<Supervisee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!scopeReady) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [tasks, fromAppointments] = await Promise.all([
        fetchMissingDocumentTasks(medplum, scopedClinicianIds),
        fetchMissingProgressNotesFromAppointments(medplum, scopedClinicianIds, {
          treatUnsignedAsMissing: true,
        }),
      ]);

      let mapped = tasks
        .map(taskToMissingDocumentItem)
        .filter((item): item is MissingDocumentItem => item !== null);
      mapped = mapped.filter((item) => scopedClinicianIds.includes(item.clinicianId));

      const apptDeduped = filterAppointmentItemsDedupedAgainstTasks(fromAppointments, mapped);
      const combined = [...mapped, ...apptDeduped].filter((item) => {
        if (item.category !== 'missing-notes') return true;
        if (viewerPractitionerId && item.clinicianId === viewerPractitionerId) return false;
        return true;
      });

      const labeled = await (async () => {
        const staffLabels = await loadPractitionerStaffLabelMap(
          medplum,
          combined.map((item) => item.clinicianId),
        );
        return combined.map((item) => {
          const live = item.clinicianId ? staffLabels.get(item.clinicianId) : undefined;
          return live ? { ...item, clinicianName: live } : item;
        });
      })();

      setItems(labeled);
      const fromItems = deriveSuperviseesFromItems(labeled);
      const byId = new Map(fromItems.map((s) => [s.id, s]));
      for (const option of superviseeOptions) {
        if (!byId.has(option.id)) {
          byId.set(option.id, {
            id: option.id,
            name: option.name,
            role: 'Clinician',
            unsignedCount: 0,
            overdueCount: 0,
          });
        }
      }
      setSupervisees([...byId.values()]);
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load missing documents'));
      setItems([]);
      setSupervisees([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, scopeReady, scopedClinicianIds, viewerPractitionerId, superviseeOptions]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { items, supervisees, loading, error, refetch: doFetch };
}
